<template>
  <div class="why-step min-h-full">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="text-lg font-medium text-gray-900">WHY - Projet associé</h2>
      <BookCheckIcon v-show="isStepValid" class="w-5 h-5 text-green-500" />
    </div>



    <div class="mt-8 space-y-6" :class="{ 'opacity-90': readOnlyConfig?.isReadOnly }">
      <!-- Section Projet avec dropdown -->
      <div class="space-y-4">
        <h3 class="text-lg font-medium text-gray-900">Projet</h3>

        <div class="relative">
          <div class="relative flex items-center">
            <!-- Input de recherche -->
            <input v-if="!selectedProject" v-model="searchQuery" type="text"
              class="w-full p-2 border border-gray-300 rounded-md mb-0 no-margin-bottom"
              placeholder="Rechercher un projet..." @input="handleSearch" @focus="handleFocus" @blur="handleBlur" />

            <!-- Affichage du projet sélectionné -->
            <div v-if="selectedProject"
              class="w-full p-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
              <div class="flex-1">
                <div class="font-medium text-gray-900">
                  {{ selectedProject.title }}
                </div>
                <div class="text-sm text-gray-600 flex items-center gap-2">
                  <span v-if="selectedProject.type" class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    {{ selectedProject.type }}
                  </span>
                  <span v-if="selectedProject.acronym">{{ selectedProject.acronym }}</span>
                </div>
              </div>

              <!-- Actions pour le projet sélectionné -->
              <div class="flex items-center gap-2">
                <!-- Badge public si applicable -->
                <span v-if="selectedProject.is_public"
                  class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  <GlobeIcon class="h-3 w-3 mr-1" />
                  Public
                </span>

                <!-- Bouton d'édition (seulement pour mes projets) -->
                <button v-if="canEditSelectedProject" title="Modifier le projet"
                  class="p-2 text-blue-500 hover:text-blue-700" @click="editProject">
                  <PencilIcon class="h-4 w-4" />
                </button>

                <!-- Bouton de réinitialisation -->
                <button v-if="!readOnlyConfig?.isReadOnly" title="Retirer le projet"
                  class="p-2 text-red-500 hover:text-red-700" @click="handleClear">
                  <XIcon class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Dropdown avec sections séparées -->
          <div v-if="showProjectsDropdown && !selectedProject && !readOnlyConfig?.isReadOnly"
            class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">

            <!-- Bouton créer -->
            <div title="Créer un nouveau projet"
              class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
              @click="openCreator">
              <PlusIcon class="h-4 w-4" />
              <span>Créer un nouveau projet</span>
            </div>

            <!-- Section : Mes projets -->
            <div v-if="myProjects.length > 0" class="section">
              <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
                <UserIcon class="h-4 w-4 text-blue-600" />
                <span class="text-sm font-medium text-blue-900">Mes projets ({{ myProjects.length }})</span>
              </div>
              <div v-for="project in myProjects" :key="project.uuid"
                class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-blue-500"
                @click="selectProject(project)">
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ project.title }}</div>
                  <div class="text-sm text-gray-600 flex items-center gap-2">
                    <span v-if="project.type" class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      {{ project.type }}
                    </span>
                    <span v-if="project.acronym">{{ project.acronym }}</span>
                  </div>
                  <p v-if="project.description" class="text-sm text-gray-500 mt-1 line-clamp-2">
                    {{ project.description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Section : Projets publics -->
            <div v-if="publicProjects.length > 0" class="section">
              <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                <GlobeIcon class="h-4 w-4 text-emerald-600" />
                <span class="text-sm font-medium text-emerald-900">Projets publics ({{ publicProjects.length }})</span>
              </div>
              <div v-for="project in publicProjects" :key="project.uuid"
                class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
                @click="selectProject(project)">
                <div class="flex-1">
                  <div class="font-medium text-gray-900 flex items-center gap-2">
                    {{ project.title }}
                    <span
                      class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                      <GlobeIcon class="h-3 w-3 mr-1" />
                      Public
                    </span>
                  </div>
                  <div class="text-sm text-gray-600 flex items-center gap-2">
                    <span v-if="project.type" class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      {{ project.type }}
                    </span>
                    <span v-if="project.acronym">{{ project.acronym }}</span>
                  </div>
                  <p v-if="project.description" class="text-sm text-gray-500 mt-1 line-clamp-2">
                    {{ project.description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Message si aucun projet -->
            <div v-if="myProjects.length === 0 && publicProjects.length === 0" class="p-4 text-center text-gray-500">
              <div class="flex flex-col items-center gap-2">
                <FolderIcon class="h-8 w-8 text-gray-400" />
                <span>Aucun projet trouvé</span>
                <span class="text-xs">Créez-en un nouveau pour commencer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Instrumentation - Conditionnelle en readonly -->
      <div
        v-if="!readOnlyConfig?.isReadOnly || (editedFields.instrumentation_description || editedFields.instrumentation_link)"
        class="space-y-4">
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-medium text-gray-900">Instrumentation</h3>
          <span v-if="!readOnlyConfig?.isReadOnly" class="text-sm text-gray-500">(optionnel)</span>
        </div>

        <div class="space-y-4">
          <div v-if="!readOnlyConfig?.isReadOnly || editedFields.instrumentation_description">
            <label for="instrumentation_description" class="block text-sm font-medium text-gray-700 mb-1">Description de
              l'instrumentation</label>
            <textarea id="instrumentation_description" v-model="editedFields.instrumentation_description"
              name="instrumentation_description" rows="3"
              placeholder="Décrivez l'instrumentation utilisée pour ce MEMoS..." :readonly="readOnlyConfig?.isReadOnly"
              :disabled="readOnlyConfig?.isReadOnly" :class="readOnlyConfig?.isReadOnly
                ? 'bg-gray-50 border-gray-300 cursor-not-allowed'
                : 'focus:border-blue-500 focus:ring-blue-500'"
              class="block w-full rounded-md border-gray-300 shadow-sm resize-none"></textarea>
          </div>

          <div v-if="!readOnlyConfig?.isReadOnly || editedFields.instrumentation_link">
            <label for="instrumentation_link" class="block text-sm font-medium text-gray-700 mb-1">Lien vers
              l'instrumentation</label>
            <input id="instrumentation_link" v-model="editedFields.instrumentation_link" name="instrumentation_link"
              type="url" placeholder="https://exemple.com/instrumentation" :readonly="readOnlyConfig?.isReadOnly"
              :disabled="readOnlyConfig?.isReadOnly" :class="readOnlyConfig?.isReadOnly
                ? 'bg-gray-50 border-gray-300 cursor-not-allowed'
                : 'focus:border-blue-500 focus:ring-blue-500'"
              class="block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>
      </div>

      <!-- Créateur de projet -->
      <ProjectsCreator v-if="showCreator && !readOnlyConfig?.isReadOnly" :user-id="userId" :show="showCreator"
        :mode="creatorMode" :initial-data="selectedProjectForEdit || undefined" @project-created="handleProjectCreated"
        @project-updated="handleProjectUpdated" @update:show="showCreator = $event" />
    </div>
  </div>
</template>

<script lang="ts">
import type { ReadOnlyConfig } from "@/types/readOnly";
import { BookCheckIcon, FolderIcon, GlobeIcon, PencilIcon, PlusIcon, UserIcon, XIcon } from 'lucide-vue-next';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { projects } from "../../api/projects";
import type { Project } from "../../api/types/entities/project/types";
import { useProjects } from "../../composables/useAssets";
import ProjectsCreator from "../creators/ProjectsCreator.vue";

export default defineComponent({
  name: 'WhyStep',
  components: {
    BookCheckIcon,
    PencilIcon,
    XIcon,
    GlobeIcon,
    PlusIcon,
    UserIcon,
    FolderIcon,
    ProjectsCreator
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    readOnlyConfig: {
      type: Object as () => ReadOnlyConfig,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['update:model-value', 'isValid'],
  setup(props, { emit }) {
    // Utilisation du composable pour le cache avec filtre 'accessible' pour avoir tous les projets
    const {
      filteredAssets: allProjects,
      isLoading,
      addAsset,
      updateAsset,
      canEditAsset: composableCanEdit
    } = useProjects({ defaultFilter: 'accessible' });

    const selectedProject = ref<Project | null>(null);
    const projectsList = ref<Project[]>([]);
    const searchQuery = ref("");
    const showCreator = ref(false);
    const creatorMode = ref<'create' | 'edit'>('create');
    const selectedProjectForEdit = ref<Project | null>(null);
    const editedFields = ref({
      instrumentation_description: "",
      instrumentation_link: ""
    });
    const showProjectsDropdown = ref(false);

    // Computed property pour vérifier si l'étape est valide
    const isStepValid = computed(() => {
      return !!props.modelValue.project_uuid;
    });

    // Watch pour émettre l'état de validité quand il change
    watch(isStepValid, (newValue) => {
      emit('isValid', newValue);
    }, { immediate: true });

    // Computed pour séparer mes projets des publiques avec recherche
    const myProjects = computed((): Project[] => {
      const filtered = allProjects.value.filter(project => {
        // Mes projets = ceux dont je suis propriétaire
        return project.user_id === props.userId;
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(project => {
        const searchableFields = ['title', 'type', 'description', 'acronym'];
        return searchableFields.some(field => {
          const value = project[field as keyof Project];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    const publicProjects = computed((): Project[] => {
      const filtered = allProjects.value.filter(project => {
        // Projets publics = ceux qui sont publics ET qui ne sont pas à moi
        return project.is_public && project.user_id !== props.userId;
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(project => {
        const searchableFields = ['title', 'type', 'description', 'acronym'];
        return searchableFields.some(field => {
          const value = project[field as keyof Project];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    // Helper pour vérifier si on peut éditer le projet sélectionné
    const canEditSelectedProject = computed((): boolean => {
      if (!selectedProject.value) return false;
      return composableCanEdit(selectedProject.value);
    });

    const filteredProjects = computed(() => {
      if (!searchQuery.value) return projectsList.value;
      const query = searchQuery.value.toLowerCase();
      return projectsList.value.filter(project =>
        project.title.toLowerCase().includes(query) ||
        (project.type?.toLowerCase() || '').includes(query) ||
        (project.description?.toLowerCase() || '').includes(query)
      );
    });

    const loadInitialProject = async (): Promise<void> => {
      if (props.modelValue.project_uuid && !selectedProject.value) {
        // Utiliser le projet déjà disponible dans props
        if (props.modelValue.project) {
          selectedProject.value = props.modelValue.project;
          editedFields.value = {
            instrumentation_description: props.modelValue.instrumentation_description || "",
            instrumentation_link: props.modelValue.instrumentation_link || ""
          };
          return;
        }

        // Si le projet n'est pas disponible dans props, le charger depuis l'API
        try {
          const project = await projects.fetchProjectById(props.modelValue.project_uuid);
          if (project) {
            selectProject(project);
          }
        } catch (error) {
          console.error("Erreur lors du chargement du projet initial:", error);
        }
      }
    };

    const fetchProjects = async (): Promise<void> => {
      try {
        projectsList.value = await projects.fetchProjectsByUserId(props.userId);
        await loadInitialProject();
      } catch (error) {
        console.error("Erreur lors de la récupération des projets:", error);
        showNotification();
      }
    };

    const selectProject = (project: Project): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      selectedProject.value = project;

      // Initialiser les champs éditables avec les valeurs existantes
      editedFields.value = {
        instrumentation_description: props.modelValue.instrumentation_description || "",
        instrumentation_link: props.modelValue.instrumentation_link || ""
      };

      // Mettre à jour le modelValue avec l'UUID du projet, l'objet projet complet et les valeurs éditables
      emit('update:model-value', {
        ...props.modelValue,
        project_uuid: project.uuid,
        project: project,
        instrumentation_description: editedFields.value.instrumentation_description,
        instrumentation_link: editedFields.value.instrumentation_link
      });
    };

    // Surveiller les changements dans les champs éditables
    watch(editedFields, (newFields: typeof editedFields.value): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      emit('update:model-value', {
        ...props.modelValue,
        instrumentation_description: newFields.instrumentation_description,
        instrumentation_link: newFields.instrumentation_link
      });
    }, { deep: true });

    const handleClear = (): void => {
      selectedProject.value = null;
      searchQuery.value = "";
      // Ne pas effacer les champs instrumentation
      emit('update:model-value', {
        ...props.modelValue,
        project_uuid: '',
      });

      // Charger les projets après avoir effacé la sélection
      fetchProjects();
    };

    const editProject = (): void => {
      if (selectedProject.value) {
        creatorMode.value = 'edit';
        selectedProjectForEdit.value = selectedProject.value;
        showCreator.value = true;
      }
    };

    const handleProjectCreated = async (project: Project): Promise<void> => {
      showCreator.value = false;
      // Ajouter au cache
      addAsset(project);
      // Sélectionner automatiquement le nouveau projet créé
      selectProject(project);
      // Recharger les projets après la création
      await fetchProjects();
    };

    const handleProjectUpdated = async (project: Project): Promise<void> => {
      showCreator.value = false;
      // Mettre à jour le cache
      updateAsset(project);
      // Mettre à jour le projet sélectionné
      selectedProject.value = project;
      // Recharger les projets après la mise à jour
      await fetchProjects();
    };

    const showNotification = (): void => {
      // Implement notification logic here
    };

    // Nouvelles méthodes pour le dropdown
    const handleSearch = (): void => {
      showProjectsDropdown.value = true;
    };

    const handleFocus = (): void => {
      if (selectedProject.value) return;
      showProjectsDropdown.value = true;
    };

    const handleBlur = (): void => {
      // On utilise setTimeout pour permettre aux clics sur la liste d'être traités avant
      setTimeout(() => {
        showProjectsDropdown.value = false;
      }, 200);
    };

    const openCreator = (): void => {
      creatorMode.value = 'create';
      selectedProjectForEdit.value = null;
      showCreator.value = true;
      showProjectsDropdown.value = false;
    };

    // Initialiser les champs instrumentation au montage
    onMounted(async () => {
      // Initialiser les champs éditables avec les valeurs du modelValue
      editedFields.value = {
        instrumentation_description: props.modelValue.instrumentation_description || "",
        instrumentation_link: props.modelValue.instrumentation_link || ""
      };

      // Ne charger les projets que si aucun projet n'est sélectionné
      if (!props.modelValue.project_uuid) {
        await fetchProjects();
      } else {
        // Sinon, juste charger le projet initial si nécessaire
        await loadInitialProject();
      }
    });

    return {
      selectedProject,
      projectsList,
      searchQuery,
      showCreator,
      creatorMode,
      selectedProjectForEdit,
      editedFields,
      filteredProjects,
      selectProject,
      handleClear,
      editProject,
      handleProjectCreated,
      handleProjectUpdated,
      showNotification,
      isStepValid,
      showProjectsDropdown,
      // Nouvelles méthodes et computed
      myProjects,
      publicProjects,
      canEditSelectedProject,
      handleSearch,
      handleFocus,
      handleBlur,
      openCreator,
      isLoading
    };
  }
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.border-l-3 {
  border-left-width: 3px;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Forcer les styles pour les inputs et textareas dans WhyStep */
.why-step input[type="url"],
.why-step textarea {
  margin-bottom: 8px !important;
  border: 1px solid #d1d5db !important;
  /* border-gray-300 */
  border-radius: 6px !important;
  /* rounded-md */
  padding: 8px 12px !important;
  /* px-3 py-2 */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
  /* shadow-sm */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.why-step input[type="url"]:focus,
.why-step textarea:focus {
  outline: none !important;
  border-color: #3b82f6 !important;
  /* border-blue-500 */
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  /* ring-blue-500 */
}

.why-step input[type="url"]:read-only,
.why-step textarea:read-only,
.why-step input[type="url"]:disabled,
.why-step textarea:disabled {
  background-color: #f9fafb !important;
  /* bg-gray-50 */
  border-color: #d1d5db !important;
  /* border-gray-300 */
  cursor: not-allowed !important;
}

.why-step input[type="url"]::placeholder,
.why-step textarea::placeholder {
  color: #9ca3af !important;
  /* text-gray-400 */
}
</style>